import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormControl, FormGroup, FormArray, Validators } from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';
import store from 'store2';

import { StaffScheduleService } from '../../../services/staff-schedule.service';
import { UserService } from '../../../services/user.service';
import { ToastHelper } from '../../common/toaster.helper';
import { PaginatorComponent } from '../../common/paginator/paginator.component';

import {
  StaffSchedule,
  StaffScheduleEntry,
  StaffScheduleBulkSaveModel,
  DaysOfWeek,
  getDayOfWeekLabel,
  getDayOfWeekShortLabel
} from '../../../models/staff-schedule.model';

interface StaffWithSchedule {
  userID: number;
  firstName: string;
  lastName: string;
  staffName: string;
  schedules: { [key: number]: StaffSchedule | null }; // key is dayOfWeek
}

@Component({
  selector: 'app-staff-schedule',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, PaginatorComponent],
  templateUrl: './staff-schedule.component.html',
  styleUrl: './staff-schedule.component.scss'
})
export class StaffScheduleComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  // Data
  staffList: StaffWithSchedule[] = [];
  allSchedules: StaffSchedule[] = [];
  staffMembers: any[] = []; // For dropdown

  // User context - automatically loaded from store
  companyId: number = 0;
  branchId: number = 0;
  branchName: string = '';

  // State
  isLoading: boolean = false;
  daysOfWeek = DaysOfWeek;

  // Modal state
  showScheduleModal: boolean = false;
  selectedStaff: StaffWithSchedule | null = null;

  // Schedule form (for week)
  scheduleForm: FormGroup = new FormGroup({
    userID: new FormControl(null, Validators.required),
    schedules: new FormArray([])
  });

  constructor(
    private scheduleService: StaffScheduleService,
    private userService: UserService,
    private toastr: ToastHelper
  ) { }

  ngOnInit(): void {
    this.companyId = parseInt(store('companyID') || '0', 10);
    this.branchId = parseInt(store('userBranches') || '0', 10);
    this.branchName = store('branchName') || '';
    this.initScheduleForm();
    this.loadStaffMembers();
    this.loadSchedules();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ============ Form Initialization ============

  get schedulesFormArray(): FormArray {
    return this.scheduleForm.get('schedules') as FormArray;
  }

  initScheduleForm(): void {
    // Clear existing
    while (this.schedulesFormArray.length) {
      this.schedulesFormArray.removeAt(0);
    }

    // Add form group for each day of week
    this.daysOfWeek.forEach(day => {
      this.schedulesFormArray.push(new FormGroup({
        dayOfWeek: new FormControl(day.value),
        dayLabel: new FormControl(day.label),
        isActive: new FormControl(false),
        startTime: new FormControl('08:30'),
        endTime: new FormControl('17:00'),
        notes: new FormControl('')
      }));
    });
  }

  // ============ Data Loading ============

  loadStaffMembers(): void {
    if (!this.companyId || !this.branchId) return;
    
    this.userService.GetBranchUsers({
      companyID: this.companyId,
      branchID: this.branchId,
      userType: 1,
      isActive: true
    }).pipe(takeUntil(this.destroy$)).subscribe({
      next: (data) => {
        console.log('Loaded staff members:', data);
        this.staffMembers = data || [];
      },
      error: (error) => {
        console.error('Failed to load staff members', error);
      }
    });
  }

  loadSchedules(): void {
    if (!this.companyId || !this.branchId) {
      this.toastr.showWarning('Unable to determine branch. Please re-login.');
      return;
    }

    this.isLoading = true;
    this.scheduleService.getStaffSchedules({
      companyID: this.companyId,
      branchID: this.branchId,
      nextRows: 500 // Get all
    }).pipe(takeUntil(this.destroy$)).subscribe({
      next: (data) => {
        this.allSchedules = data || [];
        this.processScheduleData();
        this.isLoading = false;
      },
      error: (error) => {
        this.toastr.showError(error.message || 'Failed to load schedules');
        this.isLoading = false;
      }
    });
  }

  processScheduleData(): void {
    // Group schedules by user
    const staffMap = new Map<number, StaffWithSchedule>();

    this.allSchedules.forEach(schedule => {
      if (!staffMap.has(schedule.userID)) {
        staffMap.set(schedule.userID, {
          userID: schedule.userID,
          firstName: schedule.firstName || '',
          lastName: schedule.lastName || '',
          staffName: schedule.staffName || `${schedule.firstName} ${schedule.lastName}`,
          schedules: {}
        });
      }

      const staff = staffMap.get(schedule.userID)!;
      staff.schedules[schedule.dayOfWeek] = schedule;
    });

    this.staffList = Array.from(staffMap.values()).sort((a, b) =>
      a.staffName.localeCompare(b.staffName)
    );
  }

  // ============ Modal Actions ============

  openScheduleModal(staff: StaffWithSchedule): void {
    this.selectedStaff = staff;
    this.scheduleForm.patchValue({ userID: staff.userID });

    // Populate form with existing schedules
    this.schedulesFormArray.controls.forEach((control, index) => {
      const dayOfWeek = this.daysOfWeek[index].value;
      const existingSchedule = staff.schedules[dayOfWeek];

      if (existingSchedule) {
        control.patchValue({
          isActive: existingSchedule.isActive,
          startTime: this.formatTimeSpanToInput(existingSchedule.startTime),
          endTime: this.formatTimeSpanToInput(existingSchedule.endTime),
          notes: existingSchedule.notes || ''
        });
      } else {
        control.patchValue({
          isActive: false,
          startTime: '08:30',
          endTime: '17:00',
          notes: ''
        });
      }
    });

    this.showScheduleModal = true;
  }

  openNewScheduleModal(): void {
    this.selectedStaff = null;
    this.scheduleForm.patchValue({ userID: null });
    this.initScheduleForm();
    this.showScheduleModal = true;
  }

  closeScheduleModal(): void {
    this.showScheduleModal = false;
    this.selectedStaff = null;
  }

  saveSchedule(): void {
    if (!this.scheduleForm.valid || !this.branchId) {
      this.toastr.showWarning('Please select a staff member');
      return;
    }

    const formValue = this.scheduleForm.value;
    const schedules: StaffScheduleEntry[] = [];
    const daysToDelete: number[] = [];

    formValue.schedules.forEach((day: any) => {
      if (day.isActive) {
        schedules.push({
          dayOfWeek: day.dayOfWeek,
          startTime: day.startTime + ':00',
          endTime: day.endTime + ':00',
          isActive: true,
          notes: day.notes
        });
      } else if (this.selectedStaff) {
        // Check if this day had an existing schedule that needs to be deleted
        const existingSchedule = this.selectedStaff.schedules[day.dayOfWeek];
        if (existingSchedule?.scheduleID) {
          daysToDelete.push(existingSchedule.scheduleID);
        }
      }
    });

    if (schedules.length === 0 && daysToDelete.length === 0) {
      this.toastr.showWarning('Please enable at least one day');
      return;
    }

    this.isLoading = true;

    // First delete unchecked days that had existing schedules
    const deletePromises = daysToDelete.map(scheduleId =>
      this.scheduleService.deleteSchedule(scheduleId).toPromise()
    );

    Promise.all(deletePromises).then(() => {
      // Then save the active schedules
      if (schedules.length === 0) {
        this.toastr.showSuccess('Schedule updated successfully');
        this.closeScheduleModal();
        this.loadSchedules();
        this.isLoading = false;
        return;
      }

      const model: StaffScheduleBulkSaveModel = {
        userID: formValue.userID,
        companyID: this.companyId,
        branchID: this.branchId,
        schedules: schedules
      };

      this.scheduleService.bulkSaveSchedule(model).pipe(takeUntil(this.destroy$)).subscribe({
        next: (result) => {
          if (result.success) {
            this.toastr.showSuccess('Schedule saved successfully');
            this.closeScheduleModal();
            this.loadSchedules();
          } else {
            this.toastr.showError('Failed to save schedule');
          }
          this.isLoading = false;
        },
        error: (error) => {
          this.toastr.showError(error.message || 'Failed to save schedule');
          this.isLoading = false;
        }
      });
    }).catch(() => {
      this.toastr.showError('Failed to update schedule');
      this.isLoading = false;
    });
  }

  deleteSchedule(staff: StaffWithSchedule, dayOfWeek: number): void {
    const schedule = staff.schedules[dayOfWeek];
    if (!schedule?.scheduleID) return;

    if (!confirm(`Delete schedule for ${staff.staffName} on ${getDayOfWeekLabel(dayOfWeek)}?`)) {
      return;
    }

    this.isLoading = true;
    this.scheduleService.deleteSchedule(schedule.scheduleID).pipe(takeUntil(this.destroy$)).subscribe({
      next: (result) => {
        if (result.success) {
          this.toastr.showSuccess('Schedule deleted');
          this.loadSchedules();
        }
        this.isLoading = false;
      },
      error: (error) => {
        this.toastr.showError(error.message || 'Failed to delete schedule');
        this.isLoading = false;
      }
    });
  }

  deleteAllSchedules(staff: StaffWithSchedule): void {
    const scheduleIds = Object.values(staff.schedules)
      .filter(s => s?.scheduleID)
      .map(s => s!.scheduleID);

    if (scheduleIds.length === 0) {
      this.toastr.showWarning('No schedules to delete');
      return;
    }

    if (!confirm(`Delete ALL schedules for ${staff.staffName}? This will remove ${scheduleIds.length} schedule entries.`)) {
      return;
    }

    this.isLoading = true;
    let completed = 0;
    let hasError = false;

    scheduleIds.forEach(id => {
      this.scheduleService.deleteSchedule(id).pipe(takeUntil(this.destroy$)).subscribe({
        next: () => {
          completed++;
          if (completed === scheduleIds.length) {
            this.toastr.showSuccess('All schedules deleted');
            this.loadSchedules();
            this.isLoading = false;
          }
        },
        error: (error) => {
          if (!hasError) {
            hasError = true;
            this.toastr.showError('Failed to delete some schedules');
          }
          completed++;
          if (completed === scheduleIds.length) {
            this.loadSchedules();
            this.isLoading = false;
          }
        }
      });
    });
  }

  // ============ Helper Methods ============

  formatTimeSpanToInput(timeSpan: string | undefined): string {
    if (!timeSpan) return '08:30';
    // TimeSpan format: "HH:mm:ss" -> "HH:mm"
    return timeSpan.substring(0, 5);
  }

  formatTimeForDisplay(timeSpan: string | undefined): string {
    if (!timeSpan) return '-';
    const parts = timeSpan.split(':');
    const hours = parseInt(parts[0], 10);
    const minutes = parts[1];
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12;
    return `${displayHours}:${minutes}${ampm}`;
  }

  getDayLabel(dayOfWeek: number): string {
    return getDayOfWeekLabel(dayOfWeek);
  }

  getDayShortLabel(dayOfWeek: number): string {
    return getDayOfWeekShortLabel(dayOfWeek);
  }

  hasScheduleForDay(staff: StaffWithSchedule, dayOfWeek: number): boolean {
    return !!staff.schedules[dayOfWeek]?.isActive;
  }

  getScheduleDisplay(staff: StaffWithSchedule, dayOfWeek: number): string {
    const schedule = staff.schedules[dayOfWeek];
    if (!schedule?.isActive) return '-';
    return `${this.formatTimeForDisplay(schedule.startTime)} - ${this.formatTimeForDisplay(schedule.endTime)}`;
  }

  // Apply same schedule to all weekdays
  applyToWeekdays(): void {
    const mondayControl = this.schedulesFormArray.at(1); // Monday is index 1
    const mondayValues = mondayControl.value;

    // Apply to Tuesday through Friday (indices 2-5)
    for (let i = 2; i <= 5; i++) {
      this.schedulesFormArray.at(i).patchValue({
        isActive: mondayValues.isActive,
        startTime: mondayValues.startTime,
        endTime: mondayValues.endTime
      });
    }
  }

  // Get staff members that don't have schedules yet
  getAvailableStaff(): any[] {
    const existingUserIds = new Set(this.staffList.map(s => s.userID));
    return this.staffMembers.filter(s => !existingUserIds.has(s.userID));
  }

  // Get total scheduled days for a staff member
  getScheduledDaysCount(staff: StaffWithSchedule): number {
    return Object.values(staff.schedules).filter(s => s?.isActive).length;
  }
}
